/* ============================================================
   CULIANS — Product card gallery (reference pattern)
   ============================================================
   Hover: crossfade from the featured image to the second image.
   Arrows: thin chevrons at the image edges (visible on hover) step
   through ALL of the card's images with the same crossfade. Clicks
   on arrows never navigate. Mouse leave returns to the featured
   image.

   The card markup is Atelier's <slideshow-*> custom elements, but we
   ignore its scroll/aria machinery entirely: slides are stacked by
   culians-collection.css and visibility is driven purely by the
   [data-culians-current] attribute this script sets. The CSS keeps a
   no-JS fallback (plain hover crossfade) that deactivates the moment
   this script marks the gallery with data-culians-cards-init.
   ============================================================ */
(function () {
  'use strict';

  // Solid triangles — same arrow language as the PDP lightbox (reference).
  var SVG_LEFT =
    '<svg width="5" height="8" viewBox="0 0 5 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M4.6 0.4 0.6 4 4.6 7.6 Z" fill="currentColor"/></svg>';
  var SVG_RIGHT =
    '<svg width="5" height="8" viewBox="0 0 5 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M0.4 0.4 4.4 4 0.4 7.6 Z" fill="currentColor"/></svg>';

  function initCard(card) {
    var gallery = card.querySelector('.card-gallery');
    if (!gallery || gallery.hasAttribute('data-culians-cards-init')) return;

    var slides = Array.prototype.filter.call(
      gallery.querySelectorAll('slideshow-slide'),
      function (s) { return !s.hasAttribute('hidden'); }
    );
    if (slides.length < 2) return;

    // Phone (2026-09-09): Atelier's native scroll-snap row does the swiping
    // (real slide motion, same as the product page), and one dark segment
    // 1/N of the line width glides to the current position with the site's
    // drawer easing. No stacking, no arrows. CSS: culians-collection.css
    // "PHONE CARD SWIPE".
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (gallery.hasAttribute('data-culians-cards-swipe')) return;
      gallery.setAttribute('data-culians-cards-swipe', '');
      var track = gallery.querySelector('slideshow-slides');
      if (!track) return;
      // Finite row, as on the product page: no wrap-around.
      var sc = gallery.querySelector('slideshow-component');
      if (sc) sc.removeAttribute('infinite');
      // Slides sit off-screen horizontally, so browsers leave their lazy
      // images unfetched until the swipe starts (blank frame). Fetch all of
      // the card's images once the card is near the viewport.
      var preload = function () {
        gallery.querySelectorAll('slideshow-slide img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) { preload(); io.disconnect(); }
        }, { rootMargin: '100% 0px' });
        io.observe(gallery);
      } else {
        preload();
      }
      gallery.querySelectorAll(':scope > .culians-card-bar').forEach(function (el) { el.remove(); });
      var bar = document.createElement('div');
      bar.className = 'culians-card-bar';
      bar.setAttribute('aria-hidden', 'true');
      var seg = document.createElement('span');
      seg.className = 'culians-card-bar__seg';
      seg.style.width = (100 / slides.length) + '%';
      bar.appendChild(seg);
      gallery.appendChild(bar);
      var lastIndex = -1;
      var update = function () {
        var w = track.clientWidth || 1;
        var i = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
        if (i === lastIndex) return;
        lastIndex = i;
        seg.style.transform = 'translateX(' + (i * 100) + '%)';
      };
      track.addEventListener('scroll', update, { passive: true });
      gallery.__culiansHandlers = { scroll: update, track: track };
      update();
      return;
    }

    gallery.setAttribute('data-culians-cards-init', '');

    // Eager-load every slide image up front so stepping to a not-yet-
    // loaded slide crossfades to a painted image instead of flashing
    // a blank frame.
    gallery.querySelectorAll('slideshow-slide img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });

    // Flicker fix (2026-09-13): a fetched image inside an opacity:0 slide
    // is not painted, so the browser only DECODES it on the first frame of
    // the crossfade — one blank/white frame while the old slide is already
    // fading. Decode every slide image as soon as it has loaded (off the
    // main thread), and before stepping, wait (capped) for the target's
    // decode. Pairs with `will-change: opacity` on the slides in
    // culians-collection.css so each slide is a rasterised layer and the
    // fade runs on the compositor only.
    var decoded = [];
    function decodeImg(img) {
      if (!img || typeof img.decode !== 'function') return Promise.resolve();
      if (decoded.indexOf(img) !== -1) return Promise.resolve();
      var done = function () { if (decoded.indexOf(img) === -1) decoded.push(img); };
      var run = function () { return img.decode().then(done, done); };
      if (img.complete) return run();
      return new Promise(function (resolve) {
        img.addEventListener('load', function () { run().then(resolve); }, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }
    slides.forEach(function (s) { decodeImg(s.querySelector('img')); });

    var index = 0;

    var apply = function () {
      slides.forEach(function (s, i) {
        if (i === index) s.setAttribute('data-culians-current', '');
        else s.removeAttribute('data-culians-current');
      });
    };

    var stepToken = 0;
    function step(delta) {
      index = (index + delta + slides.length) % slides.length;
      var img = slides[index].querySelector('img');
      if (!img || decoded.indexOf(img) !== -1) { apply(); return; }
      // Not decoded yet: give it up to 120ms, then go regardless.
      var token = ++stepToken;
      var go = function () { if (token === stepToken) apply(); };
      Promise.race([decodeImg(img), new Promise(function (r) { setTimeout(r, 120); })]).then(go, go);
    }

    // Arrows — appended to the GRID ITEM, as siblings of the product
    // card link, never inside it. A button that is not a descendant of
    // the <a>/<product-card-link> cannot activate it, no matter what
    // capture-phase listeners Atelier registers — navigation from an
    // arrow click is structurally impossible. Positioning: the item is
    // a size container (see culians-collection.css); the image is a
    // fixed 4:5 portrait, so the arrow centers at 62.5cqw from the top.
    var host = card.closest('.product-grid__item') || gallery;
    // Clear stale arrows from a previous init (e.g. after a section
    // re-render replaced the gallery) so we never stack duplicates and
    // clicks always drive the CURRENT gallery's state.
    host.querySelectorAll(':scope > .culians-card-arrow').forEach(function (el) { el.remove(); });
    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'culians-card-arrow culians-card-arrow--prev';
    prev.setAttribute('aria-label', 'Previous image');
    prev.innerHTML = SVG_LEFT;
    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'culians-card-arrow culians-card-arrow--next';
    next.setAttribute('aria-label', 'Next image');
    next.innerHTML = SVG_RIGHT;

    // No pointer-event silencing needed: the arrows live OUTSIDE the
    // card link and slideshow, so nothing upstream reacts to their
    // events. (Never preventDefault a trusted pointerdown here —
    // Chrome then suppresses the click entirely.)
    function arrowHandler(delta) {
      return function (e) {
        e.preventDefault();
        e.stopPropagation();
        step(delta);
      };
    }
    prev.addEventListener('click', arrowHandler(-1));
    next.addEventListener('click', arrowHandler(1));

    // ONE hover territory: the image plus the arrows overlaying it.
    // Entering it shows the arrows and swaps to the second image;
    // moving between image and arrows changes NOTHING (no snap back
    // to the first image); leaving it hides the arrows and returns
    // to the featured image. The arrows are not descendants of the
    // card, so a CSS :hover chain can't express this — relatedTarget
    // checks + a data attribute on the item drive it instead.
    function overImageParts(el) {
      return !!el && el.nodeType === 1 && (gallery.contains(el) || !!el.closest('.culians-card-arrow'));
    }
    function hoverIn() {
      host.setAttribute('data-culians-image-hover', '');
      if (index === 0 && slides.length > 1) { index = 1; apply(); }
    }
    function hoverOut(e) {
      if (overImageParts(e.relatedTarget)) return; // still inside the territory
      host.removeAttribute('data-culians-image-hover');
      index = 0;
      apply();
    }
    [gallery, prev, next].forEach(function (el) {
      el.addEventListener('pointerenter', hoverIn);
      el.addEventListener('pointerleave', hoverOut);
    });
    gallery.__culiansHandlers = { hoverIn: hoverIn, hoverOut: hoverOut };

    host.appendChild(prev);
    host.appendChild(next);

    apply();
  }

  function initAll() {
    document.querySelectorAll('.product-card').forEach(initCard);
  }

  // Breakpoint fix (2026-10-23): the mode (phone swipe vs desktop crossfade)
  // used to be chosen once, at page load. A window opened narrow and then
  // widened (or a DevTools panel closed) kept the phone setup under the
  // desktop stacking CSS, so hover fell to the no-JS fallback and showed the
  // LAST image. Now each card is reset and re-initialised whenever the
  // viewport crosses 750px.
  function resetCard(card) {
    var gallery = card.querySelector('.card-gallery');
    if (!gallery) return;
    gallery.removeAttribute('data-culians-cards-init');
    gallery.removeAttribute('data-culians-cards-swipe');
    gallery.querySelectorAll('slideshow-slide[data-culians-current]').forEach(function (s) {
      s.removeAttribute('data-culians-current');
    });
    gallery.querySelectorAll(':scope > .culians-card-bar').forEach(function (el) { el.remove(); });
    var track = gallery.querySelector('slideshow-slides');
    if (track) track.scrollLeft = 0;
    var host = card.closest('.product-grid__item');
    if (host) {
      host.removeAttribute('data-culians-image-hover');
      host.querySelectorAll(':scope > .culians-card-arrow').forEach(function (el) { el.remove(); });
    }
    // Detach the listeners the previous mode attached to the gallery/track.
    var h = gallery.__culiansHandlers;
    if (h) {
      if (h.hoverIn) { gallery.removeEventListener('pointerenter', h.hoverIn); gallery.removeEventListener('pointerleave', h.hoverOut); }
      if (h.scroll && h.track) h.track.removeEventListener('scroll', h.scroll);
      gallery.__culiansHandlers = null;
    }
  }
  var phoneMq = window.matchMedia('(max-width: 749px)');
  var onMq = function () {
    document.querySelectorAll('.product-card').forEach(function (card) {
      resetCard(card);
      initCard(card);
    });
  };
  if (phoneMq.addEventListener) phoneMq.addEventListener('change', onMq);
  else if (phoneMq.addListener) phoneMq.addListener(onMq);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify section re-renders / infinite scroll additions.
  document.addEventListener('shopify:section:load', initAll);
  var grid = document.querySelector('.product-grid');
  if (grid && 'MutationObserver' in window) {
    new MutationObserver(function () { initAll(); }).observe(grid, { childList: true, subtree: true });
  }
})();
